/**
 * Nav drawer — click-to-open, full-height side panel for top-level menu
 * items that have a submenu (QuickPro-style "doors" pattern), instead of
 * the theme's default hover mega-menu flyout.
 *
 * This is purely additive: it doesn't touch header-menu.js or the theme's
 * hover-flyout markup. It reuses the existing `.menu-list__submenu-inner`
 * content (cloned, not moved) and the theme's own `dialog`/`.dialog-drawer`
 * primitives so the panel gets the same focus-trap, Escape-to-close,
 * scroll-lock and backdrop behavior as the cart drawer / search modal.
 *
 * The old hover flyouts are hidden purely via CSS (see nav-drawer rules in
 * factor-overrides.css), so there is nothing left for this script to
 * fight with.
 */
(function () {
  'use strict';

  const MENU_ITEM_SELECTOR = '.menu-list__list-item';
  const LINK_SELECTOR = '.menu-list__link[aria-haspopup="true"]';

  /** @type {HTMLDialogElement | null} */
  let dialogEl = null;
  let scrimEl = null;
  let bodyEl = null;
  let titleEl = null;
  let closeBtn = null;
  let lastTrigger = null;

  function buildDrawerOnce() {
    if (dialogEl) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <dialog class="nav-drawer__dialog dialog-modal dialog-drawer dialog-drawer--right" aria-label="Menu">
        <div class="nav-drawer__scrim" data-nav-drawer-scrim></div>
        <div class="nav-drawer__panel" role="document">
          <header class="nav-drawer__head">
            <p class="nav-drawer__eyebrow" data-nav-drawer-title></p>
            <button type="button" class="nav-drawer__close" aria-label="Close menu" data-nav-drawer-close>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </button>
          </header>
          <div class="nav-drawer__body" data-nav-drawer-body></div>
        </div>
      </dialog>
    `.trim();

    dialogEl = wrapper.firstElementChild;
    document.body.appendChild(dialogEl);

    scrimEl = dialogEl.querySelector('[data-nav-drawer-scrim]');
    bodyEl = dialogEl.querySelector('[data-nav-drawer-body]');
    titleEl = dialogEl.querySelector('[data-nav-drawer-title]');
    closeBtn = dialogEl.querySelector('[data-nav-drawer-close]');

    closeBtn.addEventListener('click', closeDrawer);
    scrimEl.addEventListener('click', closeDrawer);
    dialogEl.addEventListener('cancel', (e) => {
      // Let the native Escape-to-close happen, just clean up focus after.
      e.preventDefault();
      closeDrawer();
    });
    dialogEl.addEventListener('click', (event) => {
      // Clicking a real link inside the drawer should close it and navigate normally.
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (link) closeDrawer();
    });
  }

  function openDrawer(menuItem, triggerLink) {
    buildDrawerOnce();

    const submenuInner = menuItem.querySelector('.menu-list__submenu-inner');
    if (!submenuInner) return;

    bodyEl.replaceChildren(submenuInner.cloneNode(true));
    titleEl.textContent = triggerLink.querySelector('.menu-list__link-title')?.textContent?.trim() || '';
    lastTrigger = triggerLink;

    if (!dialogEl.open) {
      document.documentElement.setAttribute('scroll-lock', '');
      dialogEl.showModal();
    }
  }

  function closeDrawer() {
    if (!dialogEl || !dialogEl.open) return;

    dialogEl.classList.add('dialog-closing');
    const done = () => {
      dialogEl.classList.remove('dialog-closing');
      dialogEl.close();
      document.documentElement.removeAttribute('scroll-lock');
      lastTrigger?.focus();
    };

    let ran = false;
    dialogEl.addEventListener(
      'animationend',
      () => {
        if (ran) return;
        ran = true;
        done();
      },
      { once: true }
    );
    // Fallback in case no animation runs (e.g. reduced motion).
    setTimeout(() => {
      if (ran) return;
      ran = true;
      done();
    }, 260);
  }

  function isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  function handleMenuClick(event) {
    if (!isDesktop()) return;

    const link = event.target instanceof Element ? event.target.closest(LINK_SELECTOR) : null;
    if (!link) return;

    const menuItem = link.closest(MENU_ITEM_SELECTOR);
    if (!menuItem || !menuItem.querySelector('.menu-list__submenu-inner')) return;

    event.preventDefault();
    openDrawer(menuItem, link);
  }

  function init() {
    document.addEventListener('click', handleMenuClick);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
